/**
 * STORE-SPLIT slice 2 — the DXF TEXT-IMPORT + import-progress-lifecycle action
 * family, extracted from `editorStore.ts` (no behavior change; plan
 * 2026-06-06-fullstack-gap-closure.md STORE-SPLIT, UI_ARCHITECTURE.md §6.1;
 * slice-1 precedent: `editorStoreStyleTables.ts`).
 *
 * The family = the two TEXT-source import variants (`importDxfText`,
 * `importDxfTextAsync`) + the import-progress lifecycle trio
 * (`abortDxfImport`, `setImportProgress`, `clearImportProgress`). The two
 * `File`-handle variants (`importDxfFileAsync`, `importDxfFileAsyncReplacing`
 * — the streaming INSERT/OPEN pipelines) intentionally STAY inline in
 * `editorStore.ts`; their extraction into this module is the planned slice 3
 * ("import-core now + variants later"). They already share this slice's
 * module-level machinery through `editorStoreImportHelpers.ts` (the latch,
 * thresholds, hydrates, empty-seed predicate, merge-bump), so slice 3 is a
 * pure body move.
 *
 * `editorStore.ts` composes the slice into its `create()` initializer via
 * `...createDxfImportActionsSlice(set, get, deps)` — the standard zustand
 * slice pattern. `set`/`get` ARE the store's `setState`/`getState` (zustand
 * passes the same function references into the initializer), so the former
 * `useEditorStore.getState()` call sites inside the action bodies are
 * byte-identical in behavior as `get()`.
 *
 * ## Deps injection (shared machinery stays with its owner)
 *
 * `rebuildEntityMetadataIndexFromImport` / `…Async` bump the REACTIVE
 * `entityMetadataIndexVersion` / `renderTopologyVersion` counters via
 * `useEditorStore.setState`, so they remain module-level exports of
 * `editorStore.ts` (moving them here would create a back-edge cycle). The
 * factory receives them via `deps` — the compose site passes the same
 * function references the inline bodies called before.
 *
 * ## Dup-gate consolidation (jscpd)
 *
 * The pre-split sync/async commit `set()` bodies and success-result returns
 * were near-verbatim copies of one domain truth — "commit an INSERT-style
 * merged document: rebuild shell state, fold the heal transcript, flush
 * history, DUAL-BUMP geometry+table (+selection iff prior pickset non-empty),
 * discard any in-flight batch". Inside the >1000-line `editorStore.ts` they
 * were invisible to the jscpd gate (`.jscpd.json` `maxLines: 1000`); this
 * ≤1000-line module IS scanned, so per the CLAUDE.md Этап-1 dup rule the
 * clone is ELIMINATED via the one-truth private helpers below
 * (`buildImportMergeCommitSlice`, `buildImportMergeSuccessResult`) rather
 * than baselined. The helpers reproduce the pre-split per-action semantics
 * EXACTLY — same `set()` shapes, same ordering; the single per-action
 * difference (the async path also stamps the 100% "sync" progress into the
 * SAME commit) is an explicit parameter (`syncProgressComplete`), never
 * behavior drift. The two still-inline `File` variants in `editorStore.ts`
 * carry the same commit shape (INSERT twin + the epoch-minting OPEN variant);
 * folding them onto these helpers happens when they move here (slice 3).
 *
 * ## Function dependency map
 *
 * createDxfImportActionsSlice(set, get, deps)
 *   importDxfText (sync OPEN/INSERT from a DXF source string)
 *     -> importGeometryDocumentFromDxfText (dxfGeometry.ts)
 *     -> isDocumentEffectivelyEmpty / mergeImportedGeometryDocument
 *     -> healImportedDocument (referenceHealWiring.ts)
 *     -> set(buildImportMergeCommitSlice)
 *     -> clearCompactCache + populateCompactCacheFromDocument (mutationStore.ts)
 *     -> deps.rebuildEntityMetadataIndexFromImport
 *     -> populateDependencyIndexFromDocument (mutationStore.ts)
 *     -> hydrateSysvarsFromImportedDocument / hydrateCeColorFromImportedDocument
 *        (empty-seed OPEN only)
 *   importDxfTextAsync (worker-backed, progress-emitting variant)
 *     -> startDxfImportInWorker (dxfWorkerClient.ts) | main-thread fallback
 *     -> computeCoordinateOriginForImport -> setCoordinateOrigin (first import)
 *     -> mergeImportedGeometryDocumentAsync / healImportedDocument
 *     -> populateCompactCacheFromDocumentAsync
 *     -> set(buildImportMergeCommitSlice) [with 100% sync progress]
 *     -> deps.rebuildEntityMetadataIndexFromImportAsync
 *   abortDxfImport -> currentImportHandleRef (editorStoreImportHelpers.ts)
 *   setImportProgress / clearImportProgress -> importProgress slice patch
 */
import {
  bumpForClasses,
  computeGlobalPercent,
  type BumpClassSet,
  type DrawingDocument,
  type ImportPhaseName,
  type RevisionCounters,
} from "@dxf-ai-editor/drawing-model";
import { type EditorShellState } from "@dxf-ai-editor/contracts";
import { t } from "../i18n";
import {
  importGeometryDocumentFromDxfText,
  importGeometryDocumentFromDxfTextWithProgress,
  type DxfImportResult,
} from "./dxfGeometry";
import {
  isWorkerSupported,
  startDxfImportInWorker,
} from "./dxfWorkerClient";
import {
  clearCompactCache,
  clearEntityProvenance,
  DEFAULT_DOCUMENT_ID,
  populateCompactCacheFromDocument,
  populateCompactCacheFromDocumentAsync,
  populateDependencyIndexFromDocument,
  setActiveDocument,
} from "./mutationStore";
import {
  healImportedDocument,
  type ImportHealResult,
} from "./referenceHealWiring";
import {
  mergeImportedGeometryDocument,
  mergeImportedGeometryDocumentAsync,
  syncShellStateFromMergedGeometryDocument,
} from "./storeHelpers";
import { pushAuditEvent } from "./reducerHelpers";
import {
  getCoordinateOrigin,
  setCoordinateOrigin,
} from "../scene3d/coordinateOrigin";
import {
  markStart as section0MarkStart,
  measureSince as section0MeasureSince,
} from "./section0Instrumentation";
import {
  computeCoordinateOriginForImport,
  currentImportHandleRef,
  HEAL_TRANSCRIPT_COMMAND,
  hydrateCeColorFromImportedDocument,
  hydrateSysvarsFromImportedDocument,
  importMergeBumpForPriorSelection,
  INITIAL_IMPORT_PROGRESS,
  isDocumentEffectivelyEmpty,
  type ImportProgress,
} from "./editorStoreImportHelpers";

/**
 * The store surface this slice reads and writes — a STRUCTURAL SUBSET of
 * `EditorStore` (editorStore.ts). Member types must stay IDENTICAL to the host
 * store's declarations so `Partial<DxfImportHostStore>` stays assignable to
 * `Partial<EditorStore>` at the compose site (and `EditorStore` stays
 * assignable to this type for the `set`/`get` parameters). Declared here (not
 * imported from editorStore.ts) so the module has NO back-edge into
 * `editorStore.ts` — the deps graph stays acyclic.
 */
interface DxfImportHostStore {
  state: EditorShellState;
  geometryDocument: DrawingDocument;
  importProgress: ImportProgress;
  revisions: RevisionCounters;
  pendingBumpClasses: BumpClassSet | null;
  pendingDirectSetBumpClasses: BumpClassSet | null;
  pendingBatchBaseDoc: DrawingDocument | null;
  pendingBatchBaseState: EditorShellState | null;
  pendingBatchBaseAuditSequence: number | null;
  lastBatchClosedBy: "commit" | "abort" | "discard" | null;
}

/** The zustand `setState` of the host store, narrowed to this slice's usage. */
type DxfImportSet = (
  updater: (
    store: DxfImportHostStore,
  ) => DxfImportHostStore | Partial<DxfImportHostStore>,
) => void;

/** The zustand `getState` of the host store. */
type DxfImportGet = () => DxfImportHostStore;

/**
 * Shared editorStore-owned machinery the slice calls but does NOT own (see the
 * module JSDoc). The compose site passes the module-level
 * `rebuildEntityMetadataIndexFromImport` / `…Async` exports of
 * `editorStore.ts` verbatim.
 */
export interface DxfImportActionsDeps {
  /** PR-V1 — sync metadata-index rebuild (the sync `importDxfText` path). */
  rebuildEntityMetadataIndexFromImport: (document: DrawingDocument) => void;
  /** Phase 1 (П35Р) — async chunked rebuild (the async import paths). */
  rebuildEntityMetadataIndexFromImportAsync: (
    document: DrawingDocument,
  ) => Promise<void>;
}

/**
 * The action surface this slice contributes to `EditorStore`. `editorStore.ts`
 * intersects this into its `EditorStore` type, so external importers of
 * `EditorStore` / `useEditorStore` see the exact same action signatures as
 * before the extraction.
 */
export interface DxfImportActionsSlice {
  importDxfText: (source: string) => DxfImportResult;
  /**
   * Progress-aware variant of `importDxfText`. Emits phase-by-phase
   * progress updates to `state.importProgress` so the UI can render an
   * AutoCAD-style "Processing Objects" dialog. The Promise resolves
   * with the same `DxfImportResult` shape as the sync version.
   *
   * When `Worker` is available (production browsers) the heavy parser
   * phases (tokenize → entities → bridge) run in a dedicated Web Worker
   * so the main thread stays responsive for 11 MB files. In jsdom /
   * SSR / older browsers the call transparently falls back to the
   * synchronous in-process pipeline.
   */
  importDxfTextAsync: (source: string) => Promise<DxfImportResult>;
  /**
   * Cancel the in-flight `importDxfTextAsync`, if any. The Promise
   * returned by that call will reject with an AbortError and the
   * progress dialog will unmount. No-op when no import is running.
   *
   * Also bound to `window.editorAbortImport` in App.tsx so the
   * Cancel button inside the `ImportProgressDialog` can call it
   * without prop-drilling.
   */
  abortDxfImport: () => void;
  /**
   * Partial-patch the import progress slice. Used by the async import
   * path after every phase boundary so the progress dialog redraws.
   */
  setImportProgress: (next: Partial<ImportProgress>) => void;
  /**
   * Reset the import progress slice and mark it inactive — UI will
   * unmount the progress dialog. Called on both success and error
   * paths of `importDxfTextAsync`.
   */
  clearImportProgress: () => void;
}

/**
 * One-truth commit slice for an INSERT-style import MERGE — the exact `set()`
 * payload the pre-split sync/async text-import bodies each built inline.
 *
 * - Rebuilds shell state from the merged document with `assumeValid: true`
 *   (bridge already validated each entity; the redundant Zod walk blocks the
 *   main thread ~30 sec on 685k-entity files).
 * - PR1b Phase 2a — folds the heal summary into the import-baseline state
 *   (same `set()`), so the first (empty) history entry already reflects
 *   healed refs and undo can never step back to a dangling state.
 * - Phase 4.1.j — clears history on import: existing entries reference
 *   entities from the replaced document and would corrupt replay. depthCap is
 *   user-configurable (UNDOLIMIT) and must survive the flush.
 * - ABB-S1 G3 #5 — an INSERT-style MERGE folds the imported entities + tables
 *   into the LIVE document IN PLACE: a MUTATION, not a swap → DUAL-BUMP
 *   geometry + table (+ snapshot once), do NOT mint a new documentEpoch (the
 *   doc id is preserved). History is flushed here, so clear any in-flight
 *   batch accumulator (both — round-3 F2).
 * - PR #362 round-5 (F2) — selection bumps ONLY if the merge actually changes
 *   it: it clears the pickset unconditionally, so a real change happens iff
 *   the PRIOR selection was non-empty (empty→empty = no-op).
 * - round-4 GENERALIZATION 2 — a merge flushes history; drop the base.
 * - Gate #4 round-2 (FIX C) — an import/merge clears `history.current`
 *   WITHOUT a commit: any in-flight batch's staged telemetry is DISCARDED.
 * - `syncProgressComplete` — the async path stamps the 100% "sync" progress
 *   into the SAME commit (the sync path has no progress slice at all).
 */
function buildImportMergeCommitSlice(
  store: Pick<DxfImportHostStore, "state" | "revisions">,
  mergedGeometryDocument: DrawingDocument,
  heal: Pick<ImportHealResult, "summary" | "tone">,
  options: { syncProgressComplete: boolean },
): Partial<DxfImportHostStore> {
  let nextState = syncShellStateFromMergedGeometryDocument(
    store.state,
    mergedGeometryDocument,
    { assumeValid: true },
  );
  if (heal.summary) {
    nextState = pushAuditEvent(
      nextState,
      HEAL_TRANSCRIPT_COMMAND,
      heal.summary,
      heal.tone,
    );
  }
  const committed: Partial<DxfImportHostStore> = {
    state: {
      ...nextState,
      history: {
        past: [],
        future: [],
        current: null,
        depthCap: store.state.history.depthCap,
        droppedCount: 0,
      },
    },
    geometryDocument: mergedGeometryDocument,
    revisions: bumpForClasses(
      store.revisions,
      importMergeBumpForPriorSelection(store.state.selection.entityIds),
    ),
    pendingBumpClasses: null,
    pendingDirectSetBumpClasses: null,
    pendingBatchBaseDoc: null,
    pendingBatchBaseState: null,
    pendingBatchBaseAuditSequence: null,
    lastBatchClosedBy: "discard",
  };
  if (options.syncProgressComplete) {
    committed.importProgress = {
      phase: "sync",
      percent: 100,
      itemsProcessed: 1,
      itemsTotal: 1,
      active: true,
    };
  }
  return committed;
}

/**
 * One-truth success result for an INSERT-style text import — the exact
 * `{ ok: true }` return the pre-split sync/async bodies each built inline
 * (identical strings, counted from the IMPORTED document's inline arrays).
 */
function buildImportMergeSuccessResult(
  importedGeometryDocument: DrawingDocument,
): DxfImportResult {
  const importedEntityCount = importedGeometryDocument.spaces.reduce(
    (count, space) => count + space.entities.length,
    0,
  );
  return {
    ok: true,
    message: `Imported ${importedEntityCount} supported entit${importedEntityCount === 1 ? "y" : "ies"} into the current drawing across ${importedGeometryDocument.spaces.length} space${importedGeometryDocument.spaces.length === 1 ? "" : "s"}.`,
    entityCount: importedEntityCount,
    spaceCount: importedGeometryDocument.spaces.length,
  };
}

/**
 * Factory for the DXF text-import + import-progress action family. Composed
 * into the `editorStore.ts` initializer via
 * `...createDxfImportActionsSlice(set, get, deps)`.
 */
export function createDxfImportActionsSlice(
  set: DxfImportSet,
  get: DxfImportGet,
  deps: DxfImportActionsDeps,
): DxfImportActionsSlice {
  return {
    importDxfText: (source) => {
      try {
        // Phase 4 Wave 2 Task 2 — route the compact-cache populate +
        // metadata-index updates through the active document holder.
        // Phase 4 Wave 2 (codex F3 follow-up) — key the holder by the
        // CURRENT document's id so multi-doc sessions don't all collide
        // under `DEFAULT_DOCUMENT_ID`. When `geometryDocument.id` is the
        // single-doc default we still resolve to the default holder; for
        // the multi-doc future every doc gets its own holder.
        const currentStore = get();
        const importDocId =
          currentStore.geometryDocument.id || DEFAULT_DOCUMENT_ID;
        setActiveDocument(importDocId);
        const importedGeometryDocument = importGeometryDocumentFromDxfText(
          source,
          {
            documentId: currentStore.geometryDocument.id,
            title: currentStore.geometryDocument.title,
          },
        );
        // Codex #342 round-6 P2 (F2): an INSERT-style import must NOT reset live
        // sysvars — only a FRESH OPEN (or INSERT into a blank seed) hydrates them.
        // Capture whether the CURRENT document was an empty seed BEFORE the merge
        // rebuilds it; the hydrate below is gated on this flag.
        const currentIsEmptySeedSync = isDocumentEffectivelyEmpty(
          currentStore.geometryDocument,
        );
        // GitHub Codex review PR #582 finding 2 — an empty-seed import
        // REPLACES the document but REUSES its id (the bypass below keeps
        // `id: currentStore.geometryDocument.id`), so the per-entity
        // provenance ledger — keyed by that id — would survive into a
        // document it does not describe. Its tombstones name entities that
        // no longer exist, and imported entities WITHOUT a DXF handle are
        // minted dense ids (`dxfBridge.spaces.ts`'s `nextId`: "line-1",
        // "line-2", …) that collide with them exactly, so a freshly
        // imported LINE could inherit an erased authored line's
        // classification. Reset it HERE — the one point that knows this is
        // a replacement and not an INSERT/merge (a merge must PRESERVE the
        // surviving host entities' provenance). Runs before the commit
        // `set()` below rebuilds the shell previews, which read the ledger.
        if (currentIsEmptySeedSync) {
          clearEntityProvenance(importDocId);
        }
        // Defect #8 (SYNC OPEN raw-drop) — an empty-seed OPEN must BYPASS the
        // merge, exactly like the two ASYNC empty-seed OPEN paths
        // (`{...importedGeometryDocument, id, title, savedView}`). The merge
        // (`mergeImportedGeometryDocument`) spreads `...currentDocument` (the empty
        // host, which carries NO rawPassthroughSections / rawTableGroups /
        // rawObjectGroups) and DROPS the imported raw HEADER/TABLES/OBJECTS streams,
        // yet still stamps `provenance:"imported"` on the empty-seed result. That
        // combination (marker present + raw empty) makes the next save's
        // from-scratch detection FALSE → the skeleton is skipped → the carry-forward
        // writer emits NO HEADER/tables/OBJECTS and keeps the non-hex ids. Bypassing
        // the merge keeps the imported raw streams whole so the marker⇔raw invariant
        // holds (a save round-trips the imported conformant DXF). A true INSERT
        // (non-empty host) still merges (folds entities into the from-scratch host
        // WITHOUT the imported raw, so the host stays from-scratch on save).
        const mergedRawDocument: DrawingDocument = currentIsEmptySeedSync
          ? {
              ...importedGeometryDocument,
              id: currentStore.geometryDocument.id,
              title: currentStore.geometryDocument.title,
              savedView: currentStore.geometryDocument.savedView,
            }
          : mergeImportedGeometryDocument(
              currentStore.geometryDocument,
              importedGeometryDocument,
              // Codex #343 round-1 FINDING 1 — pass the authoritative empty-seed
              // flag so an INSERT into a non-empty doc PRESERVES the host $CECOLOR
              // (matches the MIRRTEXT/OSMODE hydrate gate below). Reached ONLY on a
              // true INSERT now (empty-seed OPEN takes the bypass above), so always
              // `false` — kept explicit for the merge's CECOLOR gate.
              currentIsEmptySeedSync,
            );

        // PR1b Phase 2a — heal dangling symbol-table references (dangling
        // layer→"0", style→"Standard", …) on the WHOLE document BEFORE it is
        // populated into the cache + committed to the store. Heal is initial-
        // state normalisation (plan §12 / Codex#2 P2-5): the healed doc becomes
        // the import baseline, so the first (empty) history entry already
        // reflects healed refs and undo can never step back to a dangling state.
        const importHeal = healImportedDocument(mergedRawDocument);
        const mergedGeometryDocument = importHeal.document;

        // The commit `set()` — see `buildImportMergeCommitSlice` for the
        // history-flush / DUAL-BUMP / batch-discard invariants it preserves.
        set((store) =>
          buildImportMergeCommitSlice(store, mergedGeometryDocument, importHeal, {
            syncProgressComplete: false,
          }),
        );

        // Phase 4 Wave 2 (codex F2 follow-up) — text-import previously
        // skipped the compact-cache populate. The first dispatched
        // mutation would then partially fill the cache; the read-through
        // wrapper saw `snapshot.length > 0` and started returning only
        // the touched subset, dropping every untouched entity from the
        // reactive document. Clearing + re-populating from the merged
        // document keeps the cache authoritative and flips the
        // `cachePopulatedOnce` flag so the wrapper trusts snapshots.
        clearCompactCache();
        populateCompactCacheFromDocument(mergedGeometryDocument);

        // PR-V1 — populate the EntityMetadataIndex for every entity in the
        // imported document (incl. cap-stashed entities sitting in the
        // non-reactive cache). Drives the spatial index, viewport-cull
        // renderer (PR-V2) and panels virtualization (PR-V4).
        deps.rebuildEntityMetadataIndexFromImport(mergedGeometryDocument);
        // PR2 (plan §13) — build the dependency index from the merged document.
        populateDependencyIndexFromDocument(mergedGeometryDocument);
        // Codex #342 round-6 P2 (F2): INSERT/merge must PRESERVE the live sysvar
        // store (the in-progress session is the source of truth for MIRRTEXT /
        // OSMODE / DYN*; export projects them out via `applyLiveHeaderSysvars`).
        // ONLY hydrate FROM the imported header when the prior doc was an empty
        // seed (= effectively a fresh OPEN). A real INSERT into a live drawing no
        // longer stomps a SETVAR-changed MIRRTEXT/OSMODE. The OPEN path
        // (`importDxfFileAsyncReplacing` + App.tsx file-open) hydrates
        // unconditionally; those replace the document.
        if (currentIsEmptySeedSync) {
          hydrateSysvarsFromImportedDocument(mergedGeometryDocument);
          hydrateCeColorFromImportedDocument(mergedGeometryDocument);
        }

        return buildImportMergeSuccessResult(importedGeometryDocument);
      } catch (error) {
        return {
          ok: false,
          message: error instanceof Error ? error.message : String(error),
        };
      }
    },
    importDxfTextAsync: async (source) => {
      // Phase 4 Wave 2 Task 2 — route the compact-cache + metadata-index
      // through the active document holder. Phase 4 Wave 2 (codex F3
      // follow-up) — key by the current document id so multi-doc
      // sessions stay isolated.
      {
        const currentStoreInit = get();
        const initDocId =
          currentStoreInit.geometryDocument.id || DEFAULT_DOCUMENT_ID;
        setActiveDocument(initDocId);
      }
      // Idempotency: if a previous import is still running, cancel it
      // before kicking off a new one. Prevents the store from latching
      // onto the document of the *earlier* import while we show progress
      // for the new one.
      if (currentImportHandleRef.current) {
        currentImportHandleRef.current.abort();
        currentImportHandleRef.current = null;
      }

      // Activate the progress slice BEFORE any heavy work so the
      // ImportProgressDialog mounts immediately and the user gets
      // feedback even while the decode phase runs (which is
      // synchronous and happens before the first bridge phase tick).
      set(() => ({
        importProgress: {
          phase: "decode",
          percent: 0,
          itemsProcessed: 0,
          itemsTotal: 1,
          active: true,
        },
      }));

      try {
        const currentStore = get();
        const onProgressUpdate: (
          phase: ImportPhaseName,
          itemsProcessed: number,
          itemsTotal: number,
        ) => void = (phase, itemsProcessed, itemsTotal) => {
          // Compute the global monotonic percent from phase weights.
          // Done in the UI selector instead of here so that the
          // consumer-facing `percent` is always derivable from
          // `(phase, itemsProcessed, itemsTotal)` and does not
          // depend on per-phase progression tracking in this layer.
          const percent = computeGlobalPercent(phase, itemsProcessed, itemsTotal);
          set(() => ({
            importProgress: {
              phase,
              percent,
              itemsProcessed,
              itemsTotal,
              active: true,
            },
          }));
        };

        let importedGeometryDocument: DrawingDocument;
        if (isWorkerSupported()) {
          // Heavy parse off the main thread. The worker runs the same
          // `importDxfStringToDrawingDocumentWithProgress` pipeline and
          // posts back 4 phases (tokenize / sections / entities / bridge).
          // The store owns the surrounding decode / merge / sync phases.
          const handle = startDxfImportInWorker(
            source,
            (phase, _percent, itemsProcessed, itemsTotal) => {
              onProgressUpdate(phase, itemsProcessed, itemsTotal);
            },
          );
          currentImportHandleRef.current = handle;
          try {
            const workerDocument = await handle.promise;
            // Re-stamp documentId / title so the merged document
            // inherits the current drawing's identity, matching the
            // sync path's behaviour. The worker uses the defaults from
            // `importDxfDocumentToDrawingDocument` which are the DXF's
            // own title fields — we overwrite to keep the UI's title
            // stable across imports.
            importedGeometryDocument = {
              ...workerDocument,
              id: currentStore.geometryDocument.id,
              title: currentStore.geometryDocument.title,
            };
          } finally {
            if (currentImportHandleRef.current === handle) {
              currentImportHandleRef.current = null;
            }
          }
        } else {
          // Fallback: run the parser on the main thread. Used in jsdom
          // tests, SSR and older browsers without Worker / crypto.randomUUID.
          importedGeometryDocument =
            await importGeometryDocumentFromDxfTextWithProgress(source, {
              documentId: currentStore.geometryDocument.id,
              title: currentStore.geometryDocument.title,
              onProgress: (phase, _percent, itemsProcessed, itemsTotal) => {
                onProgressUpdate(phase, itemsProcessed, itemsTotal);
              },
            });
        }

        // ── PR-A: coordinate-offset compute (first import only) ──────
        // Locks in the origin so the Three.js renderer keeps Float32
        // precision on geodetic-scale surveys. Subsequent imports
        // re-use the same offset (Q27.A — keep-first). Failure to
        // compute (no entities, NaN bbox) is silently swallowed —
        // adapters then run as identity.
        if (getCoordinateOrigin() === null) {
          const origin = computeCoordinateOriginForImport(
            importedGeometryDocument.spaces.flatMap((s) => s.entities),
          );
          if (origin !== null) {
            try {
              setCoordinateOrigin(origin);
            } catch {
              // setter throws on second call — race-safe no-op.
            }
          }
        }

        // ── Phase: merge ──────────────────────────────────────────────
        set((store) => ({
          importProgress: {
            phase: "merge",
            percent: computeGlobalPercent("merge", 0, 1),
            itemsProcessed: 0,
            itemsTotal: 1,
            active: true,
          },
          // keep geometry/state unchanged here — only the slice is updated
          state: store.state,
        }));
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
        // Phase 1.c (П35Р) — skip merge when current doc is empty seed.
        // Codex round 4 #9 (PR #87) — use cache-aware predicate so a
        // streaming-OPEN-loaded drawing (empty inline arrays, populated
        // cache + metaIdx) is not mis-classified as an empty seed.
        const currentIsEmptySeedText = isDocumentEffectivelyEmpty(
          currentStore.geometryDocument,
        );
        // GitHub Codex review PR #582 finding 2 — replacement-import twin
        // of the sync path above: same id reuse, same stale-ledger hazard,
        // same replace-vs-merge gate. See `importDxfText` for the full
        // rationale.
        if (currentIsEmptySeedText) {
          clearEntityProvenance(
            currentStore.geometryDocument.id || DEFAULT_DOCUMENT_ID,
          );
        }
        const mergedRawDocument: DrawingDocument = currentIsEmptySeedText
          ? {
              ...importedGeometryDocument,
              id: currentStore.geometryDocument.id,
              title: currentStore.geometryDocument.title,
              savedView: currentStore.geometryDocument.savedView,
            }
          : await mergeImportedGeometryDocumentAsync(
              currentStore.geometryDocument,
              importedGeometryDocument,
              // Codex #343 round-1 FINDING 1 — this branch is reached ONLY on a
              // true INSERT (the empty-seed OPEN takes the `{...imported}` bypass
              // above, carrying the imported $CECOLOR whole). So the merge here is
              // always a non-empty host → PRESERVE its $CECOLOR.
              undefined,
              false,
            );
        // PR1b Phase 2a — heal dangling refs on the whole document before
        // populate + commit; folded into the import baseline so undo can't
        // revert to a dangling-ref state (plan §12 / Codex#2 P2-5).
        const importHeal = healImportedDocument(mergedRawDocument);
        const mergedGeometryDocument = importHeal.document;
        set(() => ({
          importProgress: {
            phase: "merge",
            percent: computeGlobalPercent("merge", 1, 1),
            itemsProcessed: 1,
            itemsTotal: 1,
            active: true,
          },
        }));
        await new Promise<void>((resolve) => setTimeout(resolve, 0));

        // Phase 4 Wave 2 (codex F2 follow-up) — text-import async path
        // (worker + jsdom fallback) previously skipped the compact-cache
        // populate; the read-through wrapper would then start returning
        // partial subsets after the first mutation. Eager-populate from
        // the merged doc keeps the cache authoritative + flips the
        // populated flag.
        clearCompactCache();
        // Phase 1 (П35Р) — async chunked populate.
        await populateCompactCacheFromDocumentAsync(mergedGeometryDocument);

        // ── Phase: sync ───────────────────────────────────────────────
        set((store) => ({
          importProgress: {
            phase: "sync",
            percent: computeGlobalPercent("sync", 0, 1),
            itemsProcessed: 0,
            itemsTotal: 1,
            active: true,
          },
          state: store.state,
        }));
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
        // Section 0 — final geometryDocument + shell-state commit (text path).
        // The commit `set()` — see `buildImportMergeCommitSlice` for the
        // history-flush / DUAL-BUMP / batch-discard invariants it preserves
        // (this path also stamps the 100% "sync" progress into the commit).
        const tSetText = section0MarkStart("client:setState");
        set((store) =>
          buildImportMergeCommitSlice(store, mergedGeometryDocument, importHeal, {
            syncProgressComplete: true,
          }),
        );
        section0MeasureSince("client:setState", tSetText);
        await new Promise<void>((resolve) => setTimeout(resolve, 0));

        // PR-V1 — populate the EntityMetadataIndex. Phase 1 async chunked.
        await deps.rebuildEntityMetadataIndexFromImportAsync(
          mergedGeometryDocument,
        );
        // PR2 (plan §13) — build the dependency index from the merged document.
        populateDependencyIndexFromDocument(mergedGeometryDocument);
        // Codex #342 round-6 P2 (F2): INSERT/merge PRESERVES the live sysvar store;
        // ONLY hydrate FROM the imported header when the prior doc was an empty seed
        // (a fresh OPEN-equivalent). Reuses the `currentIsEmptySeedText` flag
        // captured above the merge so a SETVAR-changed MIRRTEXT/OSMODE survives an
        // INSERT into a live drawing.
        if (currentIsEmptySeedText) {
          hydrateSysvarsFromImportedDocument(mergedGeometryDocument);
          hydrateCeColorFromImportedDocument(mergedGeometryDocument);
        }

        // Clear progress so the dialog unmounts. A tiny delay lets users
        // briefly see the "100%" state before the dialog disappears —
        // gives a sense of completion instead of a jarring snap.
        await new Promise<void>((resolve) => setTimeout(resolve, 120));
        set(() => ({ importProgress: { ...INITIAL_IMPORT_PROGRESS } }));

        return buildImportMergeSuccessResult(importedGeometryDocument);
      } catch (error) {
        // Clear progress on error too so the dialog never lingers after
        // a failed import.
        if (currentImportHandleRef.current) {
          currentImportHandleRef.current = null;
        }
        set(() => ({ importProgress: { ...INITIAL_IMPORT_PROGRESS } }));
        const isAbort = error instanceof Error && error.name === "AbortError";
        return {
          ok: false,
          message: isAbort
            ? t("dxfio.status.aborted")
            : error instanceof Error
              ? error.message
              : String(error),
        };
      }
    },
    abortDxfImport: () => {
      // Cancel the worker-backed parse (if any). The progress dialog
      // unmount happens via the rejection path in `importDxfTextAsync`'s
      // catch block — we don't clear the progress slice here so the user
      // briefly sees "Cancelling…" if the worker takes a tick to respond.
      if (currentImportHandleRef.current) {
        currentImportHandleRef.current.abort();
      }
    },
    setImportProgress: (next) =>
      set((store) => ({
        importProgress: { ...store.importProgress, ...next },
      })),
    clearImportProgress: () =>
      set(() => ({ importProgress: { ...INITIAL_IMPORT_PROGRESS } })),
  };
}
